import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL as string | undefined;

const plans = [
  {
    name: 'Basic',
    tagline: 'Start Focusing',
    price: '$0',
    period: 'forever',
    description: 'Everything you need to get started with focused productivity.',
    features: [
      'Unlimited daily tasks',
      'Focus timer with Pomodoro',
      '7-day task history',
      'Daily mood check-ins',
      'The ONE Thing prioritization',
    ],
    cta: 'Get Started Free',
    variant: 'outline' as const,
    popular: false,
  },
  {
    name: 'Luma Pro',
    tagline: 'Master Your Flow',
    price: '$4.99',
    period: '/month',
    description: 'For power users who want the complete productivity system.',
    features: [
      'Everything in Basic, plus:',
      'Unlimited task history',
      'Advanced insights & graphs',
      'Custom tags & categories',
      'Cloud sync across devices',
      'Priority support',
      'Weekly email summaries',
    ],
    cta: 'Start 7-Day Free Trial',
    variant: 'default' as const,
    popular: true,
  },
];

export function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isMobile = useIsMobile();
  const proCtaHref = isMobile && APP_STORE_URL ? APP_STORE_URL : undefined;

  return (
    <section ref={ref} id="pricing" className="py-24 sm:py-32 relative">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free, upgrade when you're ready. No hidden fees, no surprises.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
              className="relative group"
            >
              {/* Glow effect for Pro */}
              {plan.popular && (
                <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 via-primary/20 to-primary/30 rounded-3xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity" />
              )}
              
              <div className={`relative h-full bg-card border rounded-2xl p-6 sm:p-8 ${
                plan.popular 
                  ? 'border-primary/50 shadow-lg shadow-primary/10' 
                  : 'border-border/50'
              }`}>
                {/* Popular badge */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="flex items-center gap-1.5 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
                      <Sparkles className="h-3 w-3" />
                      POPULAR
                    </div>
                  </div>
                )}
                
                {/* Plan header */}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.tagline}</p>
                </div>
                
                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{plan.description}</p>
                </div>
                
                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                        plan.popular ? 'text-primary' : 'text-muted-foreground'
                      }`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA */}
                <Button 
                  variant={plan.variant} 
                  size="lg" 
                  className="w-full group/btn"
                  asChild
                >
                  {plan.popular && proCtaHref ? (
                    <a
                      key="app-store"
                      href={proCtaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {plan.cta}
                    </a>
                  ) : (
                    <Link key="onboarding" to="/onboarding">
                      {plan.cta}
                    </Link>
                  )}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Money back guarantee */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          30-day money-back guarantee. Cancel anytime.
        </motion.p>
      </div>
    </section>
  );
}
